import { inject, Injectable } from '@angular/core';
import { getOptionalContext } from '@mkp/core/feature-interceptor';
import { CreditStatusActions } from '@mkp/credit/feature-credit-status/actions';
import { productSummaryActions } from '@mkp/product-summary/actions';
import { PublicationHistoryActions } from '@mkp/publication/feature-publication-history/actions';
import {
  CreditMapper,
  CreditRedemptionMapper,
  CreditRedemptionResource,
  CreditResource,
} from '@mkp/shared/data-access';
import {
  ActionState,
  CreditRedemptionDto,
  CreditRedemptionState,
  CreditRedemptionStopBody,
  CreditRedemptionViewModel,
  CreditState,
  RefundCredit,
  toUUID,
} from '@mkp/shared/models';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { VacancyResource } from '@mkp/vacancy/data-access';
import { Vacancy } from '@mkp/vacancy/models';
import {
  listVacancyActions,
  vacancyActions as VacancyActions,
  vacancyApiActions,
} from '@mkp/vacancy/state/actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';

@Injectable()
export class CreditRedemptionEffects {
  private actions$ = inject(Actions);
  private creditRedemptionResource = inject(CreditRedemptionResource);
  private creditResource = inject(CreditResource);
  private notificationService = inject(SnackbarService);
  private store = inject(Store);
  private vacancyResource = inject(VacancyResource);

  readonly stopCredit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PublicationHistoryActions.stopCredit),
        switchMap(({ id }) =>
          // Step 1: Fetch latest creditRedemption
          this.creditRedemptionResource.getById(id).pipe(
            switchMap((creditRedemption) => {
              if (!creditRedemption) {
                this.notificationService.show('CREDIT_REDEMPTION.NOT_FOUND_MESSAGE', {
                  state: ActionState.Error,
                });
                return of(PublicationHistoryActions.stopCreditError());
              }

              // Step 2: Update with the latest version and info
              return this.creditRedemptionResource
                .update(
                  creditRedemption.id,
                  {
                    id: creditRedemption.id,
                    _version: creditRedemption._version, // Use the latest version
                    stoppedAt: 'now',
                  } satisfies CreditRedemptionStopBody,
                  getOptionalContext()
                )
                .pipe(
                  tap(() =>
                    this.notificationService.show('CREDIT_REDEMPTION.STOP_CREDIT_SUCCESS_MESSAGE')
                  ),
                  map((response) => CreditRedemptionMapper.toViewModel(response)),
                  map((credit: CreditRedemptionViewModel) =>
                    PublicationHistoryActions.stopCreditSuccess(credit)
                  ),
                  tap((credit) => {
                    this.store.dispatch(VacancyActions.GetVacancy({ vacancyId: credit.vacancyId }));
                  }),
                  catchError(() => {
                    this.notificationService.show('REQUEST_TIMEOUT_MESSAGE', {
                      state: ActionState.Error,
                    });
                    return of(PublicationHistoryActions.stopCreditError());
                  })
                );
            }),
            catchError(() => {
              this.notificationService.show('REQUEST_TIMEOUT_MESSAGE', {
                state: ActionState.Error,
              });
              return of(PublicationHistoryActions.stopCreditError());
            })
          )
        )
      ),
    { useEffectsErrorHandler: false }
  );

  readonly reloadCredits$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PublicationHistoryActions.stopCreditSuccess),
        tap(() => listVacancyActions.loadCredits())
      ),
    { useEffectsErrorHandler: false, dispatch: false }
  );

  readonly refundCredit$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(PublicationHistoryActions.refundCredit),
        switchMap(({ creditId, id, _creditRedeemVersion, _creditVersion }: RefundCredit) =>
          this.creditResource
            .update(
              toUUID(creditId),
              { id: toUUID(creditId), _version: _creditVersion, state: CreditState.Refund },
              getOptionalContext()
            )
            .pipe(
              map((credit) => CreditMapper.toViewModel(credit)),
              switchMap((credit) => {
                this.notificationService.show('CREDIT_REDEMPTION.STOP_CREDIT_SUCCESS_MESSAGE');
                return [
                  PublicationHistoryActions.refundCreditSuccess({ id, credit }),
                  PublicationHistoryActions.stopCredit({
                    id,
                    _version: _creditRedeemVersion,
                  }),
                ];
              }),
              catchError((error) => {
                this.notificationService.show('REQUEST_TIMEOUT_MESSAGE', {
                  state: ActionState.Error,
                });
                return of(PublicationHistoryActions.refundCreditError(error));
              })
            )
        )
      ),
    { useEffectsErrorHandler: false }
  );

  readonly loadCreditRedemptionsFromVacancy$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          vacancyApiActions.loadVacancyFromGuardSuccess,
          VacancyActions.GetVacancySuccess,
          VacancyActions.CreateVacancySuccess
        ),
        map(({ vacancy }) =>
          vacancy._embedded.creditRedemptions.map((credit) =>
            CreditRedemptionMapper.toViewModel(credit)
          )
        ),
        map((credits) => CreditStatusActions.loadCreditsSuccess({ credits }))
      ),
    { useEffectsErrorHandler: false }
  );

  readonly loadCreditRedemptionsFromVacancies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(vacancyApiActions.listVacanciesSuccess, vacancyApiActions.loadMoreVacanciesSuccess),
      map(({ vacancies }) =>
        aggregateCreditRedeems(vacancies).map((credit) =>
          CreditRedemptionMapper.toViewModel(credit)
        )
      ),
      map((credits) => CreditStatusActions.loadCreditsSuccess({ credits }))
    )
  );

  readonly stopCurrentlyActiveCredits$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(productSummaryActions.stopCurrentlyActiveCredits),
        // cannot rely on vacancy
        // // need to retrieve latest version of vacancy
        // concatLatestFrom(({ vacancyId }) => [this.store.select(getVacancyById(vacancyId))]),
        switchMap(({ vacancyId }) =>
          this.vacancyResource.getById(vacancyId).pipe(
            switchMap((vacancy) => {
              if (!vacancy) {
                return of(
                  productSummaryActions.stopCurrentlyActiveCreditsError({
                    error: 'Vacancy not found',
                  })
                );
              }

              const activeCredits = vacancy._embedded.creditRedemptions.filter(
                (credit) => credit._state === CreditRedemptionState.Active
              );

              if (activeCredits.length === 0) {
                return of(productSummaryActions.stopCurrentlyActiveCreditsSuccess());
              }

              const stopRequests = activeCredits.map((credit) =>
                this.creditRedemptionResource.update(
                  credit.id,
                  {
                    id: credit.id,
                    _version: credit._version,
                    stoppedAt: 'now',
                  } satisfies CreditRedemptionStopBody,
                  getOptionalContext()
                )
              );

              return forkJoin(stopRequests).pipe(
                map(() => productSummaryActions.stopCurrentlyActiveCreditsSuccess()),
                catchError((error) =>
                  of(productSummaryActions.stopCurrentlyActiveCreditsError({ error }))
                )
              );
            }),
            catchError(() =>
              of(
                productSummaryActions.stopCurrentlyActiveCreditsError({
                  error: 'Vacancy not found',
                })
              )
            )
          )
        )
      ),
    { useEffectsErrorHandler: false }
  );
}

// aggregate and flatten the creditRedemptions from multiple vacancies
const aggregateCreditRedeems = (vacancies: Vacancy[]): CreditRedemptionDto[] => {
  return vacancies
    .map((vacancy) => vacancy._embedded.creditRedemptions)
    .reduce((acc, cur) => [...acc, ...cur], []);
};
